import { type ComponentType, type ContainerElement, type DashboardElement, type SlotElement, isContainer, isSlot } from '../types/dashboard'

import { saveConfiguration } from './persistence'

export const findElement = <T extends 'slot' | 'container'>(
  elements: DashboardElement[],
  elementId: string,
  elementType: T
): T extends 'slot' ? SlotElement : ContainerElement => {
  const indices = elementId.split('-').map(Number)

  let currentElements = elements
  let result: DashboardElement | null = null

  for (let i = 0; i < indices.length - 1; i++) {
    const container = currentElements[indices[i]]
    if (isContainer(container)) currentElements = container.children
    else throw new Error('Container not found')
  }

  const lastIndex = indices[indices.length - 1]
  const finalElement = currentElements[lastIndex]

  if (elementType === 'slot' && isSlot(finalElement)) result = finalElement
  else if (elementType === 'container' && isContainer(finalElement)) result = finalElement
  else {
    console.error('Final element not the required type', { elementType, finalElement, currentElements, elementId })
    throw new Error('Final element not the required type')
  }

  return result as T extends 'slot' ? SlotElement : ContainerElement
}

export const deepCloneElements = (elements: DashboardElement[]): DashboardElement[] => {
  return JSON.parse(JSON.stringify(elements))
}

export const deepCloneElement = (element: DashboardElement): DashboardElement => {
  return JSON.parse(JSON.stringify(element))
}

export const getParentId = (elementId: string): string => {
  const indices = elementId.split('-').map(Number)
  return indices.slice(0, indices.length - 1).join('-')
}

export interface AddElementArgs {
  elementId: string
  newComponentType: ComponentType
  direction: 'left' | 'right' | 'top' | 'bottom'
}

export const addElement = (elements: DashboardElement[], { elementId, newComponentType, direction }: AddElementArgs) => {
  const newElements = deepCloneElements(elements)
  const parentContainerId = getParentId(elementId)
  const container = findElement(newElements, parentContainerId, 'container')
  const index = container.children.findIndex((child) => child.id === elementId)

  // -~= Splitting Logic =~- //
  const shouldSplitVertically = ['top', 'bottom'].includes(direction) && container.direction === 'horizontal'
  const shouldSplitHorizontally = ['left', 'right'].includes(direction) && container.direction === 'vertical'

  if (shouldSplitVertically || shouldSplitHorizontally) {
    const mutatedElement = deepCloneElement(container.children[index])
    if (!isSlot(mutatedElement)) throw new Error('Child is not a slot, this should never happen')

    container.children[index] = {
      id: `${container.id}-${index}`,
      type: 'container' as const,
      direction: shouldSplitVertically ? 'vertical' : 'horizontal',
      children: [
        {
          id: `${container.id}-${index}-0`,
          type: 'slot' as const,
          size: 50,
          componentType: direction === 'top' ? newComponentType : mutatedElement.componentType
        },
        {
          id: `${container.id}-${index}-1`,
          type: 'slot' as const,
          size: 50,
          componentType: direction === 'top' ? mutatedElement.componentType : newComponentType
        }
      ],
      size: mutatedElement.size
    }

    return newElements
  }

  // -~= Purely Adding Logic =~- //
  const newSlotIndex = index + (['right', 'bottom'].includes(direction) ? 1 : 0)
  const sizeAdjustmentFactor = container.children.length / (container.children.length + 1)
  container.children = container.children.map((child, childIndex) => {
    const newSize = child.size * sizeAdjustmentFactor

    if (childIndex >= newSlotIndex) {
      const newId = `${container.id}-${childIndex + 1}`
      return { ...child, size: newSize, id: newId }
    }

    return { ...child, size: newSize }
  })

  const newSlot = {
    id: `${container.id}-${newSlotIndex}`,
    type: 'slot' as const,
    size: 100 / (container.children.length + 1),
    componentType: newComponentType
  }
  container.children = [...container.children.slice(0, newSlotIndex), newSlot, ...container.children.slice(newSlotIndex)]

  return newElements
}

export interface RemoveElementArgs {
  elementId: string
}

export const dfsAdjustIds = (element: DashboardElement, parentElementId?: string, index?: number) => {
  element.id = parentElementId ? `${parentElementId}-${index}` : element.id
  if (isContainer(element)) element.children.forEach((child, childIndex) => dfsAdjustIds(child, element.id, childIndex))
}

export const dfsInspectIds = (element: DashboardElement, parentElementId?: string, index?: number) => {
  if (element.id !== (parentElementId ? `${parentElementId}-${index}` : element.id)) {
    console.error(element.id, parentElementId ? `${parentElementId}-${index}` : element.id)
  }
  if (isContainer(element)) element.children.forEach((child, childIndex) => dfsInspectIds(child, element.id, childIndex))
}

export const removeElement = (elements: DashboardElement[], { elementId }: RemoveElementArgs) => {
  const newElements = deepCloneElements(elements)
  const parentElementId = getParentId(elementId)
  const container = findElement(newElements, parentElementId, 'container')
  const index = container.children.findIndex((child) => child.id === elementId)

  // -~= Removing the last slot in a container and removing the container and slot if it's the only child =~- //
  if (container.children.length === 1) {
    const parentContainerId = getParentId(parentElementId)
    const parentContainer = findElement(newElements, parentContainerId, 'container')

    parentContainer.children = parentContainer.children.filter((child) => child.id !== container.id)
    newElements.forEach((element) => dfsAdjustIds(element))

    saveConfiguration({ elements: newElements }, 'removeTile')

    return newElements
  }

  // -~= Removing the second last slot in a container and removing the container if it's the only child =~- //
  if (container.children.length === 2 && container.id !== '0') {
    const parentContainerId = getParentId(parentElementId)
    const parentContainer = findElement(newElements, parentContainerId, 'container')

    const childElement = container.children[index === 0 ? 1 : 0]

    parentContainer.children = parentContainer.children.map((child, index) => {
      if (child.id === container.id) return { ...childElement, id: `${parentContainer.id}-${index}` }
      return child
    })

    newElements.forEach((element) => dfsAdjustIds(element))
    saveConfiguration({ elements: newElements }, 'removeTile')

    return newElements
  }

  // -~= Removing a slot in a container & resizing the existing slots =~- //
  const sizeAdjustmentFactor = container.children.length / (container.children.length - 1)
  container.children = container.children
    .filter((_, childIndex) => childIndex !== index)
    .map((child, childIndex) => {
      const newSize = child.size * sizeAdjustmentFactor
      return { ...child, size: newSize, id: `${container.id}-${childIndex}` }
    })

  newElements.forEach((element) => dfsAdjustIds(element))
  saveConfiguration({ elements: newElements }, 'removeTile')

  return newElements
}

export interface HandleRowResizeArgs {
  size: number
  elementId: string
  elementType: 'slot' | 'container'
}
